import { formatDateTimeMX } from '@/shared/lib/dateUtils'
import { Badge } from '@/shared/components/Badge'
import type { ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Filter, X, AlertCircle, FileText, ChevronDown, Monitor, ArrowLeft } from 'lucide-react'
import { apiService, type AuditLog, type AuditFilters, type ApiError } from '@/services/api'
import {
  getActionConfig,
  getEntityConfig,
  formatRelativeTime,
  formatJsonForDisplay
} from '@/features/rh/lib/auditUtils'

const colorDeCodigo = (codigo: number): ColorDeBadge =>
  codigo >= 200 && codigo < 300 ? 'verde' : codigo >= 400 ? 'rojo' : 'amarillo'

const colorDeMetodo = (metodo?: string | null): ColorDeBadge => {
  switch (metodo) {
    case 'GET': return 'azul'
    case 'POST': return 'verde'
    case 'PUT': return 'amarillo'
    case 'DELETE': return 'rojo'
    default: return 'gris'
  }
}

const AuditHistoryList = () => {
  const navigate = useNavigate()
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<AuditFilters>({
    page: 1,
    limit: 50
  })

  // Load recent audit data on component mount
  useEffect(() => {
    loadRecentAuditHistory()
  }, [])

  const loadRecentAuditHistory = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await apiService.getRecentAuditHistory()
      setAuditLogs(response.data || [])
    } catch (err) {
      const apiError = err as ApiError
      setError(Array.isArray(apiError.message) ? apiError.message.join(', ') : apiError.message || 'Failed to load audit history')
      console.error('Error loading audit history:', apiError)
      setAuditLogs([]) // Ensure we always have an array
    } finally {
      setIsLoading(false)
    }
  }

  const loadFilteredAuditHistory = async (appliedFilters: AuditFilters) => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await apiService.getAuditHistoryWithFilters(appliedFilters)
      setAuditLogs(response.data || [])
    } catch (err) {
      const apiError = err as ApiError
      setError(Array.isArray(apiError.message) ? apiError.message.join(', ') : apiError.message || 'Failed to load filtered audit history')
      console.error('Error loading filtered audit history:', apiError)
      setAuditLogs([]) // Ensure we always have an array
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = () => {
    if (Object.keys(filters).length > 2) { // More than just page and limit
      loadFilteredAuditHistory(filters)
    } else {
      loadRecentAuditHistory()
    }
  }

  const handleApplyFilters = (newFilters: AuditFilters) => {
    setFilters(newFilters)
    loadFilteredAuditHistory(newFilters)
    setShowFilters(false)
  }

  const handleClearFilters = () => {
    const clearedFilters = { page: 1, limit: 50 }
    setFilters(clearedFilters)
    loadRecentAuditHistory()
    setShowFilters(false)
  }

  const toggleRowExpanded = (logId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
    }
    setExpandedRows(newExpanded)
  }

  const clearError = () => {
    setError(null)
  }

  // Filter valid logs with safety check
  const validLogs = (auditLogs || []).filter(log => 
    log && 
    log.id && 
    typeof log.id === 'string' &&
    log.timestamp &&
    typeof log.timestamp === 'string'
  )

  if (isLoading && (!auditLogs || auditLogs.length === 0)) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1 as any)}
              className={estiloDeBoton('secundario')}
            >
              ← Volver
            </button>
            <FileText className="w-6 h-6 text-gray-600" />
            <TituloDePantalla>Historial de Auditoría del Sistema</TituloDePantalla>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600">Cargando historial de auditoría...</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      )
    }

    return (
      <div className="space-y-6 mt-8 mb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(-1 as any)}
            className={estiloDeBoton('secundario')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
            <FileText className="w-6 h-6 text-gray-600" />
            <TituloDePantalla>Historial de Auditoría del Sistema</TituloDePantalla>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className={estiloDeBoton('primario')}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'girando' : ''}`} />
              <span>Actualizar</span>
            </button>
            <button
              onClick={() => setShowFilters(true)}
              className={estiloDeBoton('primario')}
            >
              <Filter className="w-4 h-4" />
              <span>Filtros</span>
            </button>
          </div>
        </div>

    {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={clearError}
            className="ml-auto p-1 text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Active Filters */}
      {Object.keys(filters).length > 2 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Filtros Activos:</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(filters).map(([key, value]) => {
                  if (key === 'page' || key === 'limit' || !value) return null
                  return (
                    <Badge key={key} color="azul">
                      {key}: {value}
                    </Badge>
                  )
                })}
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Limpiar Todo
            </button>
          </div>
        </div>
      )}

      {/* Audit History Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Historial de Auditoría</h2>
              <p className="text-sm text-gray-600">
                {validLogs.length > 0 ? `${validLogs.length} actividades recientes` : 'Registro de auditoría del sistema'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {validLogs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No se encontró historial de auditoría</p>
              <p className="text-sm text-gray-400">Las actividades del sistema aparecerán aquí cuando estén disponibles</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Desktop Table */}
              <div className="hidden md:block">
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Usuario</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acción</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Módulo</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Hora</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estado</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {validLogs.map((log) => {
                        const actionConfig = getActionConfig(log.actionDescription)
                        const entityConfig = getEntityConfig(log.module)
                        const isExpanded = expandedRows.has(log.id)
                        const ActionIcon = actionConfig.icon
                        const EntityIcon = entityConfig.icon

                        return (
                          <>
                            <tr key={log.id} className="hover:bg-[#F4F7FB] transition-colors">
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {log.user?.firstName} {log.user?.lastName}
                                  </div>
                                  <div className="text-sm text-gray-500">{log.user?.email}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-3">
                                  <div className={`p-2 rounded-lg ${actionConfig.bgColor}`}>
                                    <ActionIcon className={`w-4 h-4 ${actionConfig.color}`} />
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">
                                    {actionConfig.label}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <EntityIcon className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm text-gray-900">{entityConfig.label}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-500">
                                  {formatRelativeTime(log.timestamp)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <Badge color={colorDeCodigo(log.statusCode)}>{log.statusCode}</Badge>
                              </td>
                              <td className="px-6 py-4">
                                <button
                                  onClick={() => toggleRowExpanded(log.id)}
                                  className="p-1 rounded hover:bg-gray-100"
                                >
                                  <ChevronDown 
                                    className={`w-4 h-4 text-gray-400 transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`} 
                                  />
                                </button>
                              </td>
                            </tr>
                            {/* Expanded details row */}
                            {isExpanded && (
                              <tr key={`${log.id}-details`}>
                                <td colSpan={6} className="py-0">
                                  <div className="bg-gray-50 px-4 py-4 border-b border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-700">Fecha y Hora:</span>
                                        <p className="text-gray-600">
                                          {formatDateTimeMX(log.timestamp, { segundos: true })}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Ruta:</span>
                                        <p className="text-gray-600 font-mono">{log.apiRoute || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700">Método:</span>
                                        <p className="text-gray-600">
                                          <Badge color={colorDeMetodo(log.httpMethod)}>{log.httpMethod || 'N/A'}</Badge>
                                        </p>
                                      </div> 
                                      {log.requestData && (
                                        <div className="md:col-span-2">
                                          <span className="font-medium text-gray-700">Datos de Solicitud:</span>
                                          <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                            <pre className="whitespace-pre-wrap text-gray-800">
                                              {formatJsonForDisplay(log.requestData)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                      {log.responseData && (
                                        <div className="md:col-span-2">
                                          <span className="font-medium text-gray-700">Datos de Respuesta:</span>
                                          <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                            <pre className="whitespace-pre-wrap text-gray-800">
                                              {formatJsonForDisplay(log.responseData)}
                                            </pre>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {validLogs.map((log) => {
                  const actionConfig = getActionConfig(log.actionDescription)
                  const entityConfig = getEntityConfig(log.module)
                  const isExpanded = expandedRows.has(log.id)
                  const ActionIcon = actionConfig.icon
                  const EntityIcon = entityConfig.icon

                  return (
                    <div key={log.id} className="border border-gray-200 rounded-lg">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`p-2 rounded-lg ${actionConfig.bgColor} flex-shrink-0`}>
                              <ActionIcon className={`w-4 h-4 ${actionConfig.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {log.user?.firstName} {log.user?.lastName}
                                </span>
                                <EntityIcon className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600">{entityConfig.label}</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {actionConfig.label}
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>{formatRelativeTime(log.timestamp)}</span>
                                <Badge color={colorDeCodigo(log.statusCode)}>{log.statusCode}</Badge>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => toggleRowExpanded(log.id)}
                            className="p-1 rounded hover:bg-gray-100 ml-2"
                          >
                            <ChevronDown 
                              className={`w-4 h-4 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`} 
                            />
                          </button>
                        </div>
                      </div>
                      
                      {/* Expanded details for mobile */}
                      {isExpanded && (
                        <div className="bg-gray-50 px-4 py-4 border-t border-gray-200">
                          <div className="grid grid-cols-1 gap-3 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Fecha y Hora Completa:</span>
                              <p className="text-gray-600">
                                {formatDateTimeMX(log.timestamp, { segundos: true })}
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Ruta API:</span>
                              <p className="text-gray-600 font-mono">{log.apiRoute || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Método:</span>
                              <p className="text-gray-600">
                                <Badge color={colorDeMetodo(log.httpMethod)}>{log.httpMethod || 'N/A'}</Badge>
                              </p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Dirección IP:</span>
                              <p className="text-gray-600 font-mono">{log.ipAddress}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Agente de Usuario:</span>
                              <p className="text-gray-600 truncate" title={log.userAgent}>
                                <Monitor className="w-4 h-4 inline mr-1" />
                                {log.userAgent?.split(' ')[0] || 'N/A'}
                              </p>
                            </div>
                            {log.requestData && (
                              <div>
                                <span className="font-medium text-gray-700">Datos de Solicitud:</span>
                                <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                  <pre className="whitespace-pre-wrap text-gray-800">
                                    {formatJsonForDisplay(log.requestData)}
                                  </pre>
                                </div>
                              </div>
                            )}
                            {log.responseData && (
                              <div>
                                <span className="font-medium text-gray-700">Datos de Respuesta:</span>
                                <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                  <pre className="whitespace-pre-wrap text-gray-800">
                                    {formatJsonForDisplay(log.responseData)}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Audit History Count */}
      <div className="text-sm text-gray-500">
        Total de entradas de auditoría: {validLogs.length}
      </div>

      {/* Filters Modal */}
      {showFilters && (
        <AuditFiltersModal
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  )
}

// Filters Modal Component
interface AuditFiltersModalProps {
  currentFilters: AuditFilters
  onApplyFilters: (filters: AuditFilters) => void
  onClose: () => void
}

const AuditFiltersModal = ({ currentFilters, onApplyFilters, onClose }: AuditFiltersModalProps) => {
  const [localFilters, setLocalFilters] = useState<AuditFilters>(currentFilters)

  const handleInputChange = (key: keyof AuditFilters, value: string | number) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onApplyFilters(localFilters)
  }

  const handleClear = () => {
    setLocalFilters({ page: 1, limit: 50 })
  }

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Filtrar Historial de Auditoría</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID de Usuario
              </label>
              <input
                type="text"
                value={localFilters.userId || ''}
                onChange={(e) => handleInputChange('userId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ingrese ID de usuario"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Módulo
              </label>
              <SelectBuscable
                variante="formulario"
                value={localFilters.module || ''}
                onChange={(valor) => handleInputChange('module', valor)}
                canDelete={false}
                placeholder="Todos los Módulos"
                placeholderDeBusqueda="Buscar módulo..."
                opciones={[
                  { value: '', label: 'Todos los Módulos' },
                  { value: 'auth', label: 'Autenticación' },
                  { value: 'users', label: 'Usuarios' },
                  { value: 'job-requests', label: 'Solicitudes de Trabajo' },
                  { value: 'rbac', label: 'RBAC' },
                  { value: 'modules', label: 'Módulos' },
                  { value: 'projects', label: 'Proyectos' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Acción
              </label>
              <input
                type="text"
                value={localFilters.actionDescription || ''}
                onChange={(e) => handleInputChange('actionDescription', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="ej., CREATE, UPDATE, DELETE"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Método HTTP
              </label>
              <SelectBuscable
                variante="formulario"
                value={localFilters.httpMethod || ''}
                onChange={(valor) => handleInputChange('httpMethod', valor)}
                canDelete={false}
                placeholder="Todos los Métodos"
                placeholderDeBusqueda="Buscar método..."
                opciones={[
                  { value: '', label: 'Todos los Métodos' },
                  { value: 'GET', label: 'GET' },
                  { value: 'POST', label: 'POST' },
                  { value: 'PUT', label: 'PUT' },
                  { value: 'PATCH', label: 'PATCH' },
                  { value: 'DELETE', label: 'DELETE' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código de Estado
              </label>
              <input
                type="number"
                value={localFilters.statusCode || ''}
                onChange={(e) => handleInputChange('statusCode', parseInt(e.target.value) || '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="ej., 200, 404, 500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección IP
              </label>
              <input
                type="text"
                value={localFilters.ipAddress || ''}
                onChange={(e) => handleInputChange('ipAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="ej., 192.168.1.1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Desde
              </label>
              <input
                type="datetime-local"
                value={localFilters.dateFrom || ''}
                onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Hasta
              </label>
              <input
                type="datetime-local"
                value={localFilters.dateTo || ''}
                onChange={(e) => handleInputChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resultados por página
              </label>
              <SelectBuscable
                variante="formulario"
                value={String(localFilters.limit || 50)}
                onChange={(valor) => handleInputChange('limit', parseInt(valor))}
                canDelete={false}
                placeholderDeBusqueda="Buscar cantidad..."
                opciones={[
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                  { value: '200', label: '200' },
                ]}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClear}
              className={estiloDeBoton('secundario')}
            >
              Limpiar Todo
            </button>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={estiloDeBoton('primario')}
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </form>
      </div>
    </CapaDeModal>
  )
}

export default AuditHistoryList